import { base64DecodeUtf8 } from '~/helpers/string';

const VALID_RESUME_STEPS = ['spaces', 'assets', 'jobs'] as const;

type OnboardingResumeStep = (typeof VALID_RESUME_STEPS)[number];

export type OnboardingResumeLinkData = {
  step: OnboardingResumeStep;
  siteId: string;
  placeName: string;
  industry: string;
};

/**
 * Validates that a value matches the onboarding resume payload schema.
 * Ensures runtime type safety when parsing JSON from base64 encoded resume links.
 */
export const isValidOnboardingResumeData = (
  value: unknown
): value is OnboardingResumeLinkData => {
  if (!value || typeof value !== 'object') return false;

  const data = value as Record<string, unknown>;

  if (
    typeof data.step !== 'string' ||
    !VALID_RESUME_STEPS.includes(data.step as OnboardingResumeStep)
  ) {
    return false;
  }

  if (typeof data.siteId !== 'string' || data.siteId.length === 0) {
    return false;
  }

  if (typeof data.placeName !== 'string' || data.placeName.length === 0) {
    return false;
  }

  if (typeof data.industry !== 'string' || data.industry.length === 0) {
    return false;
  }

  return true;
};

export const isInternalUrl = (url?: string) => {
  if (!url) {
    return false;
  }

  // Same-origin relative paths only. Protocol-relative URLs (`//evil.example`)
  // also start with `/` but must not be treated as in-app navigation.
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

export const getSettingsPopupHash = (url?: string): string | null => {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url, window.location.href);
    const { hash } = parsed;

    if (
      hash &&
      (hash.startsWith('#user-settings-open') ||
        hash.startsWith('#org-settings-open') ||
        hash === '#settings-open' ||
        hash.startsWith('#settings-open:'))
    ) {
      return hash;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Parse onboarding resume hash and return the resume data
 * Format: #onboarding-resume:base64EncodedJSON
 * Validates the parsed data matches the OnboardingResumeData schema before returning.
 */
export const getOnboardingResumeData = (
  url?: string
): OnboardingResumeLinkData | null => {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url, window.location.href);
    const { hash } = parsed;

    if (hash && hash.startsWith('#onboarding-resume:')) {
      const base64Data = hash.replace('#onboarding-resume:', '');
      const jsonStr = base64DecodeUtf8(base64Data);
      const parsedData = JSON.parse(jsonStr);

      if (isValidOnboardingResumeData(parsedData)) {
        return parsedData;
      }

      console.warn('Invalid onboarding resume data format', {
        parsedData,
        url,
      });
      return null;
    }

    return null;
  } catch {
    return null;
  }
};
